import * as Yup from 'yup';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, orderBy } from 'lodash';
import { Form, Title } from '@/components';
import { Button, Space } from 'antd';
import { ProjectStepLookupState } from '@/redux';
import { FormProjectDeadlineItem } from './FormProjectDeadlineItem';
import { ProjectType } from '@/core';
import moment from 'moment';

interface Props {
  steps: ProjectStepLookupState[];
  onSubmit: (value: any) => any;
  onCancel: () => any;
  projectType: ProjectType;
}

const SCHEMA = Yup.object().shape({
  steps: Yup.array()
    .of(
      Yup.object().shape({
        initialDate: Yup.date().nullable(),
        deadlineDate: Yup.date().nullable().required(),
      }),
    )
    .required(),
});

function useInitialValue(
  steps: ProjectStepLookupState[],
  projectType: ProjectType,
) {
  return useMemo(
    () => ({
      steps: steps.map((step, index) => ({
        id: step.id,
        index: step.index,
        name: step.name,
        deadlineDate:
          !step.deadlineDate && projectType === ProjectType.Renovation
            ? moment().add(5 * (index + 1), 'year')
            : step.deadlineDate,
        initialDate: step.deadlineDate,
      })),
    }),
    [projectType, steps],
  );
}

export const FormProjectDeadlines = (props: Props) => {
  const { steps: stepsProp, onSubmit, onCancel, projectType } = props;
  const { t } = useTranslation();

  const steps = useMemo(() => orderBy(stepsProp, (x) => x.index), [stepsProp]);
  const initialValues = useInitialValue(steps, projectType);

  if (isEmpty(steps)) {
    return null;
  }

  return (
    <Form.Formik<ReturnType<typeof useInitialValue>>
      uid="project-deadlines"
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={SCHEMA}
      i18n="projects"
    >
      <Title>{t('projects.details.editDeadlinesTitle')}</Title>
      {steps.map((step) => (
        <FormProjectDeadlineItem
          key={step.index}
          name={`steps[${step.index}]`}
        />
      ))}
      <Space direction="horizontal" className="mt-3">
        <Button onClick={onCancel}>{t('cancel')}</Button>
        <Form.Submit type="primary">{t('save')}</Form.Submit>
      </Space>
    </Form.Formik>
  );
};
